import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, isErroServidor } from '@/contexts/AuthContext';
import { Eye, EyeOff, ShieldCheck, Mail, Lock, AlertCircle, KeyRound, Phone, ArrowLeft, CheckCircle2, User, LogIn } from 'lucide-react';
import axios from 'axios';
import { useLoginImagem } from '@/lib/loginImagem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function extrairErro(error, fallback) {
  const data = error.response?.data;
  if (typeof data === 'object' && typeof data?.detail === 'string') return data.detail;
  if (isErroServidor(error)) {
    return error.response
      ? 'Servidor temporariamente indisponível. Aguarde alguns segundos e tente novamente.'
      : 'Sem conexão com o servidor. Verifique sua internet.';
  }
  return fallback;
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [requires2fa, setRequires2fa] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Esqueceu a senha
  const [modoRecuperacao, setModoRecuperacao] = useState(false);
  const [recuperacaoStep, setRecuperacaoStep] = useState(1); // 1=email, 2=escolher metodo, 3=sucesso
  const [recuperacaoEmail, setRecuperacaoEmail] = useState('');
  const [recuperacaoOpcoes, setRecuperacaoOpcoes] = useState([]);
  const [recuperacaoNome, setRecuperacaoNome] = useState('');
  const [recuperacaoMensagem, setRecuperacaoMensagem] = useState('');
  
  // Senha expirada
  const { login, clearSenhaExpirada, senhaExpirada } = useAuth();
  const [senhaExpiradaMode, setSenhaExpiradaMode] = useState(senhaExpirada);
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const navigate = useNavigate();

  // Sync senhaExpiradaMode with AuthContext's senhaExpirada
  useEffect(() => {
    if (senhaExpirada) {
      setSenhaExpiradaMode(true);
    }
  }, [senhaExpirada]);

  const handleBuscarRecuperacao = async (e) => {
    e.preventDefault();
    setErro('');
    if (!recuperacaoEmail) { setErro('Informe seu e-mail'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/esqueceu-senha`, { email: recuperacaoEmail });
      setRecuperacaoOpcoes(res.data.opcoes);
      setRecuperacaoNome(res.data.nome);
      setRecuperacaoStep(2);
    } catch (error) {
      setErro(extrairErro(error, 'Erro ao buscar usuário'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarRecuperacao = async (metodo) => {
    setErro('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/recuperar-senha`, { email: recuperacaoEmail, metodo });
      setRecuperacaoMensagem(res.data.message);
      setRecuperacaoStep(3);
    } catch (error) {
      setErro(extrairErro(error, 'Erro ao enviar recuperação'));
    } finally {
      setLoading(false);
    }
  };

  const voltarLogin = () => {
    setModoRecuperacao(false);
    setRecuperacaoStep(1);
    setRecuperacaoEmail('');
    setRecuperacaoOpcoes([]);
    setRecuperacaoNome('');
    setRecuperacaoMensagem('');
    setErro('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!email || !senha) { setErro('Preencha todos os campos'); return; }
    setLoading(true);
    const result = await login(email, senha);
    setLoading(false);
    if (result.success && result.senha_expirada) {
      setSenhaExpiradaMode(true);
      setErro('');
    } else if (result.success) {
      navigate('/dashboard');
    } else {
      setErro(result.message);
    }
  };

  const handleTrocarSenha = async (e) => {
    e.preventDefault();
    setErro('');
    if (!novaSenha || novaSenha.length < 6) {
      setErro('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não conferem');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/trocar-senha-expirada`, {
        nova_senha: novaSenha,
        confirmar_senha: confirmarSenha
      });
      clearSenhaExpirada();
      navigate('/dashboard');
    } catch (error) {
      setErro(extrairErro(error, 'Erro ao alterar senha'));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)'
  };

  const bgImageUrl = useLoginImagem();

  // Tela de troca de senha expirada
  if (senhaExpiradaMode) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center relative" style={{ background: '#0a0a0a' }}>
        <img src={bgImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} />
        <div className="relative z-10 w-full flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            <div className="rounded-2xl p-10" style={{
              background: 'linear-gradient(145deg, rgba(20,20,20,0.92) 0%, rgba(10,10,10,0.95) 100%)',
              border: '1px solid rgba(251,191,36,0.3)',
              boxShadow: '0 0 40px rgba(251,191,36,0.08), inset 0 1px 0 rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
            }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" 
                  style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}>
                  <KeyRound className="w-8 h-8 text-amber-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Sua senha expirou</h1>
                <p className="text-white/40 mt-2 text-sm">Por favor, crie uma nova senha para continuar</p>
              </div>

              <form onSubmit={handleTrocarSenha} className="space-y-5" autoComplete="off">
                <div>
                  <label className="text-white/60 text-sm block mb-2">Nova Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type={mostrarNovaSenha ? 'text' : 'password'}
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full h-14 pl-12 pr-14 rounded-xl text-white placeholder-white/20 outline-none transition-all focus:ring-1 focus:ring-amber-500/50"
                      style={inputStyle}
                      autoFocus
                      data-testid="nova-senha-input"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarNovaSenha(!mostrarNovaSenha)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {mostrarNovaSenha ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-white/60 text-sm block mb-2">Confirmar Nova Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type={mostrarNovaSenha ? 'text' : 'password'}
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      placeholder="Repita a nova senha"
                      className="w-full h-14 pl-12 pr-4 rounded-xl text-white placeholder-white/20 outline-none transition-all focus:ring-1 focus:ring-amber-500/50"
                      style={inputStyle}
                      data-testid="confirmar-senha-input"
                    />
                  </div>
                </div>

                {/* Indicador de força */}
                {novaSenha && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{
                          background: novaSenha.length >= i * 3 
                            ? (novaSenha.length >= 12 ? '#22c55e' : novaSenha.length >= 8 ? '#f59e0b' : '#ef4444')
                            : 'rgba(255,255,255,0.1)'
                        }} />
                      ))}
                    </div>
                    <p className="text-xs" style={{ 
                      color: novaSenha.length >= 12 ? '#22c55e' : novaSenha.length >= 8 ? '#f59e0b' : novaSenha.length >= 6 ? '#f59e0b' : '#ef4444'
                    }}>
                      {novaSenha.length < 6 ? 'Muito curta' : novaSenha.length < 8 ? 'Aceitável' : novaSenha.length < 12 ? 'Boa' : 'Forte'}
                    </p>
                  </div>
                )}

                {erro && (
                  erro.toLowerCase().includes('usuário inativo') ? (
                    <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.35)' }} data-testid="login-usuario-inativo">
                      <p className="text-amber-300 text-sm font-semibold">Usuário inativo</p>
                      <p className="text-amber-200/90 text-xs mt-1">Solicite a ativação ao responsável (supervisor/administrador) para voltar a acessar o CRM.</p>
                    </div>
                  ) : (
                    <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)' }} data-testid="login-erro">
                      <p className="text-red-400 text-sm">{erro}</p>
                    </div>
                  )
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 rounded-xl font-bold text-black text-lg transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)', boxShadow: '0 4px 24px rgba(251,191,36,0.3)' }}
                  data-testid="trocar-senha-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Alterando...
                    </span>
                  ) : 'Alterar Senha e Entrar'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tela de recuperação de senha
  if (modoRecuperacao) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center relative" style={{ background: '#0a0a0a' }}>
        <img src={bgImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} />
        <div className="relative z-10 w-full flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            <div className="rounded-2xl p-10" style={{
              background: 'linear-gradient(145deg, rgba(20,20,20,0.92) 0%, rgba(10,10,10,0.95) 100%)',
              border: '1px solid rgba(220,38,38,0.25)',
              boxShadow: '0 0 40px rgba(220,38,38,0.08), inset 0 1px 0 rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
            }}>
              {/* Step 1: Informar email */}
              {recuperacaoStep === 1 && (
                <>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" 
                      style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)' }}>
                      <KeyRound className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white">Esqueceu a senha?</h1>
                    <p className="text-white/40 mt-2 text-sm">Informe seu e-mail cadastrado</p>
                  </div>
                  <form onSubmit={handleBuscarRecuperacao} className="space-y-5">
                    <div>
                      <label className="text-white/60 text-sm block mb-2">E-mail</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                        <input
                          type="email"
                          value={recuperacaoEmail}
                          onChange={(e) => setRecuperacaoEmail(e.target.value)}
                          placeholder="seu@email.com"
                          className="w-full h-14 pl-12 pr-4 rounded-xl text-white placeholder-white/20 outline-none transition-all focus:ring-1 focus:ring-red-500/50"
                          style={inputStyle}
                          autoFocus
                          data-testid="recuperacao-email-input"
                        />
                      </div>
                    </div>
                    {erro && (
                      <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)' }}>
                        <p className="text-red-400 text-sm">{erro}</p>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 rounded-xl font-bold text-white text-lg transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)', boxShadow: '0 4px 24px rgba(220,38,38,0.35)' }}
                      data-testid="recuperacao-buscar-btn"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                          Buscando...
                        </span>
                      ) : 'Continuar'}
                    </button>
                  </form>
                </>
              )}

              {/* Step 2: Escolher método (email ou telefone) */}
              {recuperacaoStep === 2 && (
                <>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" 
                      style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)' }}>
                      <KeyRound className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white">Recuperar senha</h1>
                    <p className="text-white/40 mt-2 text-sm">
                      Olá, <span className="text-white/70">{recuperacaoNome}</span>! Escolha como deseja receber sua nova senha:
                    </p>
                  </div>
                  <div className="space-y-3">
                    {recuperacaoOpcoes.map((opcao) => (
                      <button
                        key={opcao.tipo}
                        onClick={() => handleEnviarRecuperacao(opcao.tipo)}
                        disabled={loading}
                        className="w-full p-5 rounded-xl flex items-center gap-4 transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50 text-left"
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                        data-testid={`recuperacao-metodo-${opcao.tipo}`}
                      >
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ 
                            background: opcao.tipo === 'email' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
                            border: `1px solid ${opcao.tipo === 'email' ? 'rgba(59,130,246,0.3)' : 'rgba(16,185,129,0.3)'}`
                          }}>
                          {opcao.tipo === 'email' ? (
                            <Mail className="w-5 h-5 text-blue-400" />
                          ) : (
                            <Phone className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {opcao.tipo === 'email' ? 'Enviar por E-mail' : 'Enviar por Telefone'}
                          </p>
                          <p className="text-white/40 text-sm">{opcao.destino}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {erro && (
                    <div className="p-3 rounded-xl text-center mt-4" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)' }}>
                      <p className="text-red-400 text-sm">{erro}</p>
                    </div>
                  )}
                </>
              )}

              {/* Step 3: Sucesso */}
              {recuperacaoStep === 3 && (
                <>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" 
                      style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                      <CheckCircle2 className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white">Senha enviada!</h1>
                    <p className="text-white/50 mt-3 text-sm leading-relaxed">
                      {recuperacaoMensagem}
                    </p>
                    <p className="text-white/30 mt-4 text-xs">
                      Ao fazer login com a nova senha, você será solicitado a criar uma senha definitiva.
                    </p>
                  </div>
                  <button
                    onClick={voltarLogin}
                    className="w-full h-14 rounded-xl font-bold text-white text-lg transition-all hover:brightness-110 active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)', boxShadow: '0 4px 24px rgba(220,38,38,0.35)' }}
                    data-testid="recuperacao-voltar-login-btn"
                  >
                    Voltar para o Login
                  </button>
                </>
              )}

              {/* Voltar */}
              {recuperacaoStep !== 3 && (
                <div className="text-center mt-6">
                  <button 
                    onClick={voltarLogin}
                    className="text-white/40 text-sm hover:text-white/60 transition-colors inline-flex items-center gap-1"
                    data-testid="recuperacao-voltar-btn"
                  >
                    <ArrowLeft size={14} />
                    Voltar para o login
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-end relative" style={{ background: '#0a0a0a' }}>
      {/* Fundo - Brasil preenchendo tela toda */}
      <img src={bgImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />

      {/* Formulário no lado direito */}
      <div className="relative z-10 flex flex-col items-center justify-center px-8 mr-8 lg:mr-16">
        <div className="w-full" style={{ maxWidth: '380px' }}>
          {/* Card login */}
          <div className="rounded-2xl p-8 pb-6" style={{
            background: 'rgba(15,15,20,0.65)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
          }}>
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-white">Bem-vindo(a)!</h1>
              <p className="text-white/50 mt-1 text-sm">Faca login para acessar o CRM</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              {/* Usuário */}
              <div>
                <label className="text-white/80 text-sm font-medium block mb-2">Usuario</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Digite seu usuario ou e-mail"
                    className="w-full h-12 pl-12 pr-4 rounded-xl text-white placeholder-white/30 outline-none transition-all focus:ring-1 focus:ring-purple-500/50"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Senha */}
              <div>
                <label className="text-white/80 text-sm font-medium block mb-2">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="Digite sua senha"
                    className="w-full h-12 pl-12 pr-14 rounded-xl text-white placeholder-white/30 outline-none transition-all focus:ring-1 focus:ring-purple-500/50"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  >
                    {mostrarSenha ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Esqueceu a senha - posicionado à direita */}
              <div className="text-right -mt-1">
                <button
                  type="button"
                  onClick={() => { setModoRecuperacao(true); setErro(''); }}
                  className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                  data-testid="esqueceu-senha-link"
                >
                  Esqueceu sua senha?
                </button>
              </div>

              {/* Erro */}
              {erro && (
                <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)' }}>
                  <p className="text-red-400 text-sm">{erro}</p>
                </div>
              )}

              {/* Botão Entrar - gradiente vermelho/roxo */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-bold text-white text-base transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #db2777 50%, #7c3aed 100%)', boxShadow: '0 4px 20px rgba(219,39,119,0.35)' }}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Entrando...
                  </span>
                ) : (
                  <>
                    <LogIn size={18} />
                    Entrar
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <p className="text-white/30 text-sm">&copy; 2026 <span className="text-white/50 font-medium">Multi Telecom</span> - Todos os direitos reservados.</p>
      </div>
    </div>
  );
};

export default Login;
